// TermsAndConditionsPage.tsx

import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import GavelIcon from "@mui/icons-material/Gavel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShieldIcon from "@mui/icons-material/Shield";
import { useTranslation } from "react-i18next";

export const TermsAndConditionsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeSection, setActiveSection] = useState<string>("fair-use");

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6, color: "text.primary" }}>
      {/* Header Banner */}
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: "bold", color: "text.primary" }}>
          {t("pages.termsAndConditions.header.title")}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
          {t("pages.termsAndConditions.header.subtitle")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Sidebar Navigation (Sticky) */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2,
              position: { md: "sticky" },
              top: { md: 24 },
              borderRadius: 1,
              bgcolor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: "bold", color: "text.primary" }}>
              {t("pages.termsAndConditions.nav.title")}
            </Typography>
            <List component="nav" disablePadding>
              <ListItemButton
                selected={activeSection === "fair-use"}
                onClick={() => scrollToSection("fair-use")}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.12),
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.18),
                    },
                  },
                }}
              >
                <LibraryBooksIcon
                  fontSize="small"
                  sx={{ mr: 2, color: activeSection === "fair-use" ? "primary.main" : "action.active" }}
                />
                <ListItemText primary={t("pages.termsAndConditions.nav.fairUse")} />
              </ListItemButton>

              <ListItemButton
                selected={activeSection === "affiliation"}
                onClick={() => scrollToSection("affiliation")}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.12),
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.18),
                    },
                  },
                }}
              >
                <ShieldIcon
                  fontSize="small"
                  sx={{ mr: 2, color: activeSection === "affiliation" ? "primary.main" : "action.active" }}
                />
                <ListItemText primary={t("pages.termsAndConditions.nav.affiliation")} />
              </ListItemButton>

              <ListItemButton
                selected={activeSection === "liability"}
                onClick={() => scrollToSection("liability")}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.12),
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.18),
                    },
                  },
                }}
              >
                <WarningAmberIcon
                  fontSize="small"
                  sx={{ mr: 2, color: activeSection === "liability" ? "primary.main" : "action.active" }}
                />
                <ListItemText primary={t("pages.termsAndConditions.nav.liability")} />
              </ListItemButton>

              <ListItemButton
                selected={activeSection === "acceptance"}
                onClick={() => scrollToSection("acceptance")}
                sx={{
                  borderRadius: 1,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.12),
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.18),
                    },
                  },
                }}
              >
                <GavelIcon
                  fontSize="small"
                  sx={{ mr: 2, color: activeSection === "acceptance" ? "primary.main" : "action.active" }}
                />
                <ListItemText primary={t("pages.termsAndConditions.nav.acceptance")} />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ p: 4, borderRadius: 1, bgcolor: "background.paper", borderColor: "divider" }}
          >
            {/* Section 1 */}
            <Box id="fair-use" component="section" sx={{ mb: 5 }}>
              <Typography variant="h5" gutterBottom sx={{ color: "primary.main", fontWeight: "bold" }}>
                {t("pages.termsAndConditions.section1.title")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 1.5 }}>
                {t("pages.termsAndConditions.section1.paragraph1")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 1.5 }}>
                {t("pages.termsAndConditions.section1.paragraph2")}
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.7 }}>
                <li>{t("pages.termsAndConditions.section1.bullet1")}</li>
                <li>{t("pages.termsAndConditions.section1.bullet2")}</li>
                <li>{t("pages.termsAndConditions.section1.bullet3")}</li>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: "divider" }} />

            {/* Section 2 */}
            <Box id="affiliation" component="section" sx={{ mb: 5 }}>
              <Typography variant="h5" gutterBottom sx={{ color: "primary.main", fontWeight: "bold" }}>
                {t("pages.termsAndConditions.section2.title")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 1.5 }}>
                {t("pages.termsAndConditions.section2.paragraph1")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                {t("pages.termsAndConditions.section2.paragraph2")}
              </Typography>
            </Box>

            <Divider sx={{ my: 4, borderColor: "divider" }} />

            {/* Section 3 */}
            <Box id="liability" component="section" sx={{ mb: 5 }}>
              <Typography variant="h5" gutterBottom sx={{ color: "primary.main", fontWeight: "bold" }}>
                {t("pages.termsAndConditions.section3.title")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 2 }}>
                {t("pages.termsAndConditions.section3.paragraph1")}
              </Typography>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.error.main, isDark ? 0.2 : 0.08),
                  borderLeft: "4px solid",
                  borderColor: "error.main",
                  p: 2,
                  my: 2,
                  borderRadius: "0 8px 8px 0",
                }}
              >
                <Typography variant="body2" sx={{ color: "error.main", fontWeight: "medium" }}>
                  <strong>{t("pages.termsAndConditions.section3.releaseLabel")}</strong>{" "}
                  {t("pages.termsAndConditions.section3.releaseBody")}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: "divider" }} />

            {/* Section 4 */}
            <Box id="acceptance" component="section">
              <Typography variant="h5" gutterBottom sx={{ color: "primary.main", fontWeight: "bold" }}>
                {t("pages.termsAndConditions.section4.title")}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                {t("pages.termsAndConditions.section4.paragraph1")}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};
